import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import TpcDisclaimer from '@/components/TpcDisclaimer';
import messages from '@/i18n/catalogs/corporate/SellingPointsSection';
import { tpchCopy } from '@/data/hardcharger-launch';
import React from 'react';

import { Link } from '@/i18n/router';
import { Button } from '@/components/ui/button';
import { Server, Gauge, Plug, Unlock, ShieldCheck, ArrowRight } from 'lucide-react';
import AnimatedSection from './AnimatedSection';
import SectionHeading from '@/components/SectionHeading';

interface Pillar {
  icon: React.ElementType;
  title: string;
  body: React.ReactNode;
  accent: string;
}



const SellingPointsSection = () => {
  const m = useMessages(messages);
  const { locale } = useLocale();
  const tpch = tpchCopy(locale);
const pillars: Pillar[] = [
  {
    icon: Server,
    title: m.s1,
    accent: 'text-sky-400',
    body: (
      <>{m.s2}<span className="text-foreground font-medium">{m.s3}</span>{m.s4}</>
    ),
  },
  {
    icon: Gauge,
    title: m.s5,
    accent: 'text-emerald-400',
    body: (
      <>
        {locale === 'pt-BR' ? 'Com tecnologia DuckDB — ' : 'Powered by DuckDB — the '}
        <span className="text-foreground font-medium">{locale === 'pt-BR' ? '2ª melhor mediana com cache aquecido no ClickBench' : '#2 median hot-run on ClickBench'}</span>
        {locale === 'pt-BR'
          ? `, benchmark de referência em nó único, à frente de Snowflake, BigQuery, Redshift e Databricks. Uma carga de trabalho de 1 TB derivada do TPC-H roda em menos de ${tpch.hcSecondsUnder} segundos por `
          : `'s single-node reference benchmark, ahead of Snowflake, BigQuery, Redshift & Databricks. A 1 TB workload derived from TPC-H runs in under ${tpch.hcSecondsUnder} seconds for `}
        <span className="text-foreground font-medium">{tpch.hcCostRounded}</span>
        {locale === 'pt-BR' ? ` de computação bruta sob demanda na AWS em uma única máquina, com o ${tpch.hcLabel}.` : ` of raw AWS on-demand compute on one machine, with ${tpch.hcLabel}.`}
      </>
    ),
  },
  {
    icon: Plug,
    title: m.s9,
    accent: 'text-violet-400',
    body: (
      <>{m.s10}<span className="text-foreground font-medium">{m.s11}</span>{m.s12}</>
    ),
  },
  {
    icon: Unlock,
    title: m.s13,
    accent: 'text-amber-400',
    body: (
      <>{m.s14}<span className="text-foreground font-medium">{m.s15}</span>{m.s16}</>
    ),
  },
];

  return (
    <section className="bg-background py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fade-up">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <SectionHeading withId anchor="selling-points" className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">{m.s17}</SectionHeading>
            <p className="text-xl text-muted-foreground">{m.s18}</p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <AnimatedSection key={pillar.title} animation="fade-up" className="h-full">
                <div
                  className="group h-full rounded-2xl border border-border bg-card p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-glow"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 rounded-xl border border-border bg-background/60 p-3 transition-colors group-hover:border-primary/40">
                      <Icon className={`h-6 w-6 ${pillar.accent}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">{pillar.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{pillar.body}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
        <TpcDisclaimer className="mt-4 text-muted-foreground" />

        {/* Offer band — the de-risker, high on the page */}
        <AnimatedSection animation="fade-up">
          <div className="mt-12 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-gizmo-600/90 via-indigo-700/90 to-blue-800/90 p-8 sm:p-10 shadow-glow">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
              <div className="flex items-start gap-4 flex-grow">
                <div className="flex-shrink-0 rounded-xl bg-white/15 p-3 backdrop-blur-sm">
                  <ShieldCheck className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-100/90 mb-1">{m.s19}</p>
                  <p className="text-xl md:text-2xl font-bold text-white leading-snug">{m.s20}</p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Link to="/cut-your-warehouse-bill">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-gizmo-800 hover:bg-blue-50 dark:bg-white dark:text-gizmo-800 dark:hover:bg-blue-50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    data-ga-cta="cut_my_bill"
                    data-ga-location="home_selling_points"
                  >{m.s21}<ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default SellingPointsSection;
